import Router from '@/client/router';
import { MobileNavigationContext } from './mobileNavigation';
/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import { useErrorBoundary } from 'preact/hooks';

// Core
import useContext, { ClientContext } from '@/client/context';
import { useToast } from '@/client/components/ui/Toast';

// Core components
import RouterComponent from '@client/services/router/components/router';
import { Select } from '@client/components';

// app

import type * as Models from '@models/types';

// Resources
import './index.less';
import '@/client/assets/tailwind.css';
import { LogOut, X, Bell, PanelLeftOpen } from 'lucide-preact';
import { Sidebar, SidebarBrand, SidebarCollapseToggle } from '@/client/components/ui/Sidebar';
import Chatbot from '@/client/components/ui/Chatbot';
import NavigationMenu, { buildDefaultExpandedMenus, getParentMenuIds } from './NavigationMenu';
import CrossPathLogo from '@/client/assets/identity/logo.svg';
// Removed cn import - using direct class concatenation

declare const __DEV__: boolean;

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SIDEBAR_EXPANDED_WIDTH = 'w-66';
const SIDEBAR_COLLAPSED_WIDTH = 'w-19';
const FULLSCREEN_ROUTES = ['/dashboard/edge-onboarding'];

interface DashboardLayoutProps {
    children: React.ReactNode;
    currentPage?: string;
}
const NoRouteLoader = () => null;

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, currentPage = 'dashboard' }) => {
    const { user, app, api, Router, page } = useContext();
    const toast = useToast();
    const readPathname = React.useCallback(
        () =>
            typeof window === 'undefined'
                ? page?.context?.request.path ?? Router.context?.request.path ?? ''
                : window.location.pathname,
        [Router, page],
    );
    const [notifications, setNotifications] = React.useState<{ list: Models.Notification[]; unreadCount: number }>({
        list: [],
        unreadCount: 0,
    });
    const [pathname, setPathname] = React.useState(readPathname);
    const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
    const [notifOpen, setNotifOpen] = React.useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const [expandedMenus, setExpandedMenus] = React.useState<string[]>(() => {
        try {
            const stored = localStorage.getItem('cp_expanded_menus');
            if (stored) {
                const parsed = JSON.parse(stored);
                if (Array.isArray(parsed)) {
                    const allowedParents = getParentMenuIds(user);
                    const filtered = parsed.filter(
                        (id: unknown): id is string => typeof id === 'string' && allowedParents.includes(id),
                    );
                    if (filtered.length > 0) {
                        return filtered;
                    }
                }
            }
        } catch {}
        return buildDefaultExpandedMenus(user);
    });
    const [navigationLabels, setNavigationLabels] = React.useState<Record<string, string>>({ dashboard: 'Dashboard' });
    const registerNavigationItem = React.useCallback((id: string, label: string) => {
        setNavigationLabels((prev) => {
            if (prev[id] === label) {
                return prev;
            }
            return { ...prev, [id]: label };
        });
    }, []);
    const logout = () => {
        window.location.href = '/auth/logout';
    };
    const markAllRead = () => {};

    // Render bug report
    // TIDO: move to framework
    useErrorBoundary((e) => {
        app.reportBug({ before: 'Render page', stacktrace: e.stack }).then(() => {
            console.error(e);

            // Reload page
            if (__DEV__) {
                console.warn(`The app would be reloaded in production mode.`);
                toast.error(`Errur during page rendering: ${e.message}`);
                console.error(e);
            } else toast.error(`An issue occurred while loading this page. Our dev team will solve it asap.`);
        });
    });

    // Persist expanded menus and keep them stable across navigations
    React.useEffect(() => {
        try {
            localStorage.setItem('cp_expanded_menus', JSON.stringify(expandedMenus));
        } catch {}
    }, [expandedMenus]);

    React.useEffect(() => {
        const syncPathname = () => {
            setPathname(readPathname());
            setMobileMenuOpen(false);
        };
        syncPathname();
        return Router.on('page.changed', syncPathname);
    }, [Router, readPathname]);

    React.useEffect(() => {
        if (!mobileMenuOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setMobileMenuOpen(false);
        };
        const onResize = () => {
            if (window.innerWidth >= 1024) setMobileMenuOpen(false);
        };
        document.addEventListener('keydown', onEscape);
        window.addEventListener('resize', onResize);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onEscape);
            window.removeEventListener('resize', onResize);
        };
    }, [mobileMenuOpen]);

    // When user permissions change, validate previously saved menus against available parents
    React.useEffect(() => {
        const possibleParents = getParentMenuIds(user);
        setExpandedMenus((prev) => {
            const filtered = prev.filter((id) => possibleParents.includes(id));
            if (filtered.length > 0) {
                return filtered;
            }
            return buildDefaultExpandedMenus(user).filter((id) => possibleParents.includes(id));
        });
    }, [user?.roles, user?.type]);
    const toggleMenu = (menuId: string) => {
        setExpandedMenus((prev) => (prev.includes(menuId) ? prev.filter((id) => id !== menuId) : [...prev, menuId]));
    };
    const isMenuExpanded = (menuId: string) => expandedMenus.includes(menuId);
    const currentPageLabel = navigationLabels[currentPage] || 'Dashboard';
    const agencyName = user?.managedAgency?.name;
    const brandLabel = agencyName || 'CrossPath';
    const isFullscreenRoute = FULLSCREEN_ROUTES.includes(pathname);

    if (isFullscreenRoute) {
        return <RouterComponent service={app.Router} loaderComponent={NoRouteLoader} />;
    }

    return (
        <div className="min-h-screen bg-gradient-background">
            {/* Sidebar */}
            <div
                className={`fixed left-0 top-0 z-40 hidden h-full border-r border-slate-300 shadow-[5px_0_12px_rgba(15,23,42,0.05)] transition-all duration-300 lg:block ${sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH}`}
            >
                <Sidebar collapsed={sidebarCollapsed}>
                    {/* Brand strip — fully hidden when collapsed; only the expand toggle remains */}
                    {!sidebarCollapsed && (
                        <div className="flex items-center gap-1 border-b border-orange-100 bg-linear-to-br from-orange-50 via-white to-pink-50 px-3 pb-4 pt-4">
                            <div className="min-w-0 flex-1">
                                <SidebarBrand
                                    logo={
                                        <img
                                            src={CrossPathLogo}
                                            alt="CrossPath Logo"
                                            className="h-full w-full rounded-xl object-contain"
                                        />
                                    }
                                    name={brandLabel}
                                    data-testid="nav-brand-label"
                                />
                            </div>
                            <SidebarCollapseToggle
                                collapsed={sidebarCollapsed}
                                onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
                            />
                        </div>
                    )}

                    {/* Collapsed: the brand mark keeps the corner, and reveals the expand control on hover. */}
                    {sidebarCollapsed && (
                        <div className="flex justify-center border-b border-orange-100 bg-linear-to-br from-orange-50 via-white to-pink-50 px-2 py-3">
                            <button
                                type="button"
                                onClick={() => setSidebarCollapsed(false)}
                                aria-label="Expand sidebar"
                                title="Expand sidebar"
                                data-testid="sidebar-expand-toggle"
                                className="group/expand relative flex h-11 w-11 items-center justify-center rounded-xl shadow-[0_6px_16px_rgba(15,23,42,0.16)] ring-1 ring-black/[0.06] transition-transform duration-300 ease-out hover:scale-105 focus:outline-none"
                            >
                                <img
                                    src={CrossPathLogo}
                                    alt="CrossPath"
                                    className="h-full w-full rounded-xl object-contain transition-opacity duration-200 group-hover/expand:opacity-0 group-focus-within/expand:opacity-0"
                                />
                                <span className="absolute inset-0 flex items-center justify-center rounded-xl bg-slate-900/90 text-white opacity-0 transition-opacity duration-200 group-hover/expand:opacity-100 group-focus-within/expand:opacity-100">
                                    <PanelLeftOpen className="h-4.5 w-4.5" strokeWidth={1.75} />
                                </span>
                            </button>
                        </div>
                    )}

                    {/* Navigation */}
                    <NavigationMenu
                        user={user}
                        sidebarCollapsed={sidebarCollapsed}
                        isMenuExpanded={isMenuExpanded}
                        onToggleMenu={toggleMenu}
                        registerNavigationItem={registerNavigationItem}
                        currentPathname={pathname}
                        onLogout={logout}
                        onRequestExpand={() => setSidebarCollapsed(false)}
                    />
                </Sidebar>
            </div>

            {/* Main Content */}
            <div className={`min-w-0 pb-14 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-19' : 'lg:ml-66'}`}>
                <MobileNavigationContext.Provider value={() => setMobileMenuOpen(true)}>
                    <RouterComponent service={app.Router} loaderComponent={NoRouteLoader} />
                </MobileNavigationContext.Provider>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-[60] lg:hidden">
                    <button type="button" className="absolute inset-0 bg-slate-950/45" onClick={() => setMobileMenuOpen(false)} aria-label="Close navigation" />
                    <div role="dialog" aria-modal="true" aria-label="Navigation" className={`absolute left-0 top-0 flex h-full max-w-[calc(100vw-3rem)] flex-col bg-white shadow-xl ${SIDEBAR_EXPANDED_WIDTH}`}>
                        <div className="flex items-center justify-between gap-3 border-b border-orange-100 bg-linear-to-br from-orange-50 via-white to-pink-50 px-3 py-3">
                            <SidebarBrand
                                logo={
                                    <img
                                        src={CrossPathLogo}
                                        alt="CrossPath Logo"
                                        className="h-full w-full rounded-xl object-contain"
                                    />
                                }
                                name={brandLabel}
                                data-testid="nav-brand-label"
                            />
                            <button
                                type="button"
                                aria-label="Close navigation"
                                onClick={() => setMobileMenuOpen(false)}
                                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-primary-400 transition-colors hover:bg-primary-50 hover:text-primary-700"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <NavigationMenu
                            user={user}
                            sidebarCollapsed={false}
                            isMenuExpanded={isMenuExpanded}
                            onToggleMenu={toggleMenu}
                            registerNavigationItem={registerNavigationItem}
                            currentPathname={pathname}
                            variant="mobile"
                            onNavigate={() => setMobileMenuOpen(false)}
                        />

                        {/* Mobile Logout Button */}
                        <div className="shrink-0 p-4 border-t border-slate-200">
                            <button
                                onClick={() => {
                                    setMobileMenuOpen(false);
                                    logout();
                                }}
                                className="mx-auto flex h-10 w-10 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-600 transition-all hover:bg-red-100 hover:text-red-700 hover:shadow-sm"
                                data-testid="logout-button"
                                aria-label="Logout"
                                title="Logout"
                            >
                                <LogOut size={20} />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Chatbot */}
            <Chatbot currentPage={currentPage} />
        </div>
    );
};
export default DashboardLayout;
